import { useState, useEffect, useRef } from "react";
import { Avatar, Group, Text, Menu, rem, Button, Modal, Badge, Divider } from "@mantine/core";
import { IconLogout, IconChevronDown } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { useUser } from "../tools/Context";
import { get, post } from "./Request";

export default function Header() {
  const [userMenuOpened, setUserMenuOpened] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [modalOpened, setModalOpened] = useState(false);
  const { clearUserData } = useUser();
  const navigate = useNavigate();
  const user = useUser();
  const { email, role, token, isratp } = useUser();
  const socket = useRef(null);

  const groupNotifications = (notifications) => {
    const grouped = {};
    notifications.forEach((notification) => {
      const key = `${notification.name}-${notification.link}`;
      if (grouped[key]) {
        grouped[key].count += 1;
      } else {
        grouped[key] = { ...notification, count: 1 };
      }
    });
    return Object.values(grouped);
  };

  const fetchNotifications = async () => {
    try {
      const response = await get("notification/notifications", { user: { id: user.id } }, token);
      const groupedNotifications = groupNotifications(response);
      setNotifications(groupedNotifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();

    const protocol = window.location.protocol === "https:" ? "wss://" : "ws://";
    const socketUrl = `${protocol}${window.location.host}/api/`;
    socket.current = new WebSocket(socketUrl);

    socket.current.onopen = () => {
      console.log("WebSocket connected");
    };

    socket.current.onmessage = (event) => {
      console.log("WebSocket message received:", event.data);
      fetchNotifications();
    };

    socket.current.onclose = () => {
      console.log("WebSocket disconnected");
    };

    return () => {
      if (socket.current) {
        socket.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, token]);

  const handleDisconnectClick = () => {
    clearUserData();
    showNotification({
      title: "Utilisateur déconnecté avec succès",
      autoClose: 3000,
      color: "teal",
    });
    navigate("/login");
  };

  const handleClearAll = async () => {
    try {
      await post("notification/notifications/clear", { userId: user.id }, token);
      setNotifications([]);
      showNotification({
        title: "Toutes les notifications ont été supprimées",
        autoClose: 3000,
        color: "teal",
      });
    } catch (error) {
      console.error("Erreur suppression notifications:", error);
      showNotification({
        title: "Échec suppression des notifications",
        autoClose: 3000,
        color: "red",
      });
    }
  };

  return (
    <Group h="100%" px="md">
      <Group justify="space-between" style={{ flex: 1 }}>
        <Text fw={700} ml={20} style={{ fontSize: "1.8rem" }}>
          Module OS de Cepaj
        </Text>
        <Group ml="xl" gap={15} visibleFrom="sm">
          <Text>{role}</Text>

          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{ transition: "pop-top-right" }}
            onClose={() => setUserMenuOpened(false)}
            onOpen={() => setUserMenuOpened(true)}
            withinPortal
          >
            <Menu.Target>
              <Group gap={7} style={{ cursor: "pointer" }}>
                <Avatar
                  src={"https://raw.githubusercontent.com/mantinedev/mantine/master/.demo/avatars/avatar-5.png"}
                  alt={user.firstName}
                  radius="xl"
                  size={20}
                />
                <Text fw={500} size="sm" lh={1} mr={3}>
                  {email}
                </Text>
                <IconChevronDown style={{ width: rem(12), height: rem(12) }} stroke={1.5} />
              </Group>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={handleDisconnectClick}
                leftSection={<IconLogout style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
              >
                Déconnexion
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
          {notifications.length > 0 && (
            <Badge
              color={isratp ? "#0a0082" : "#ff7e2e"}
              onClick={() => setModalOpened(true)}
              style={{ cursor: "pointer" }}
            >
              {notifications.reduce((sum, notification) => sum + notification.count, 0)}
            </Badge>
          )}
        </Group>
      </Group>
      <Modal
        size="auto"
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title={
          <Group justify="space-between" w="100%">
            <Text fw={600}>Notifications</Text>
            <Button variant="light" size="xs" onClick={handleClearAll} disabled={notifications.length === 0}>
              Tout effacer
            </Button>
          </Group>
        }
      >
        <Divider />
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <Group justify="space-between" key={notification.id} mt="xs">
              <Group>
                <Text>{notification.name}</Text>
                {notification.count > 1 && <Badge>{notification.count}</Badge>}
              </Group>
              <Button
                component="a"
                href={notification.link}
                variant="outline"
                size="compact-md"
                onClick={async () => {
                  if (notification.type === "view") {
                    try {
                      await post(`notification/notifications/delete/${notification.id}`, {}, token);
                      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
                      showNotification({
                        title: "Notification supprimée",
                        autoClose: 3000,
                        color: "teal",
                      });
                    } catch (error) {
                      console.error("Erreur suppression notification:", error);
                      showNotification({
                        title: "Échec suppression notification",
                        autoClose: 3000,
                        color: "red",
                      });
                    }
                  }
                }}
              >
                Voir
              </Button>
              <Divider mt={7} />
            </Group>
          ))
        ) : (
          <Text>Aucune notification</Text>
        )}
      </Modal>
    </Group>
  );
}
